/**
 * DNA Connect Admin → Central de Documentos
 * Permite publicar contratos, circulares, materiais, arquivos para famílias.
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  FileText, Plus, Trash2, Edit, Upload, Eye, Download, Loader2, FileType, FileSpreadsheet, FileImage,
} from 'lucide-react';
import { DnaTableV4 } from '@/design-system/DnaDesignSystem';

const CATEGORIAS = [
  { value: 'contrato', label: 'Contratos' },
  { value: 'circular', label: 'Circulares' },
  { value: 'material', label: 'Materiais' },
  { value: 'arquivo', label: 'Arquivos' },
];

const AUDIENCIAS = [
  { value: 'all', label: 'Todas as famílias' },
  { value: 'class_group', label: 'Por turma' },
  { value: 'student', label: 'Por aluno' },
  { value: 'segment', label: 'Por segmento' },
];

const STATUS = [
  { value: 'draft', label: 'Rascunho', color: 'bg-slate-200 text-slate-700' },
  { value: 'published', label: 'Publicado', color: 'bg-emerald-100 text-emerald-700' },
];

const ALLOWED_EXT = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png', 'xls', 'xlsx'];
const MAX_MB = 20;

const iconForFile = (name) => {
  const ext = (name || '').split('.').pop()?.toLowerCase();
  if (['xls', 'xlsx'].includes(ext)) return <FileSpreadsheet className="w-4 h-4 text-emerald-600" />;
  if (['jpg', 'jpeg', 'png'].includes(ext)) return <FileImage className="w-4 h-4 text-rose-500" />;
  if (['doc', 'docx'].includes(ext)) return <FileType className="w-4 h-4 text-blue-600" />;
  return <FileText className="w-4 h-4 text-purple-600" />;
};

const empty = {
  title: '', description: '', category: 'circular',
  audience_type: 'all', class_group_ids: [], student_ids: [], segment: [],
  file_url: '', file_name: '', file_type: '', file_size: 0,
  status: 'draft', notify_families: false,
};

export default function DocumentosAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroCat, setFiltroCat] = useState('all');
  const [filtroStatus, setFiltroStatus] = useState('all');
  const [filtroQ, setFiltroQ] = useState('');
  const [classGroups, setClassGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroCat !== 'all') params.category = filtroCat;
      if (filtroStatus !== 'all') params.status = filtroStatus;
      if (filtroQ) params.q = filtroQ;
      const r = await api.get('/dnaconnect/admin/documents', { params });
      setItems(r?.data?.items || []);
    } catch (e) {
      toast.error('Falha ao listar documentos: ' + (e?.response?.data?.detail || e?.message));
    } finally { setLoading(false); }
  }, [filtroCat, filtroStatus, filtroQ]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => {
    api.get('/class-groups').then(r => setClassGroups(Array.isArray(r.data) ? r.data : (r.data?.items || []))).catch(() => {});
  }, []);

  const openNew = () => { setEditing(null); setForm({ ...empty }); setOpen(true); };
  const openEdit = (doc) => {
    setEditing(doc);
    setForm({
      ...empty, ...doc,
      class_group_ids: doc.class_group_ids || [],
      student_ids: doc.student_ids || [],
      segment: doc.segment || [],
    });
    setOpen(true);
  };

  const handleFile = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error(`Formato .${ext} não suportado. Use PDF, DOC/DOCX, JPG/PNG, XLS/XLSX.`);
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Arquivo maior que ${MAX_MB}MB.`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/dnaconnect/admin/documents/upload', fd);
      setForm(f => ({
        ...f,
        file_url: r.data.file_url, file_name: r.data.file_name,
        file_type: r.data.file_type, file_size: r.data.file_size,
      }));
      toast.success('Arquivo enviado.');
    } catch (e) {
      toast.error('Falha no upload: ' + (e?.response?.data?.detail || e?.message));
    } finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Título obrigatório.'); return; }
    if (!form.file_url) { toast.error('Faça upload de um arquivo antes de salvar.'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/dnaconnect/admin/documents/${editing.id}`, form);
        toast.success('Documento atualizado.');
      } else {
        await api.post('/dnaconnect/admin/documents', form);
        toast.success('Documento criado.');
      }
      setOpen(false);
      fetchList();
    } catch (e) {
      toast.error('Falha ao salvar: ' + (e?.response?.data?.detail || e?.message));
    } finally { setSaving(false); }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Excluir "${doc.title}"?`)) return;
    try {
      await api.delete(`/dnaconnect/admin/documents/${doc.id}`);
      toast.success('Documento excluído.');
      fetchList();
    } catch (e) {
      toast.error('Falha ao excluir: ' + (e?.response?.data?.detail || e?.message));
    }
  };

  const apiBase = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <div className="space-y-4" data-testid="documentos-admin">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="flex items-center gap-2 text-purple-800">
              <FileText className="w-5 h-5" /> Central de Documentos
            </CardTitle>
            <Button onClick={openNew} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-novo-documento">
              <Plus className="w-4 h-4 mr-1" /> Novo Documento
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
            <Input placeholder="Buscar por título…" value={filtroQ} onChange={e => setFiltroQ(e.target.value)} data-testid="filtro-q" />
            <Select value={filtroCat} onValueChange={setFiltroCat}>
              <SelectTrigger data-testid="filtro-cat"><SelectValue placeholder="Categoria" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as categorias</SelectItem>
                {CATEGORIAS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filtroStatus} onValueChange={setFiltroStatus}>
              <SelectTrigger data-testid="filtro-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                {STATUS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchList} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Atualizar'}
            </Button>
          </div>

          <DnaTableV4 testId="documentos-admin-table-v4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Documento</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Público</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Criado em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={6} className="text-center text-slate-500">Carregando…</TableCell></TableRow>
                )}
                {!loading && items.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-slate-500" data-testid="empty">Nenhum documento.</TableCell></TableRow>
                )}
                {items.map((d, i) => {
                  const stCfg = STATUS.find(s => s.value === d.status) || STATUS[0];
                  return (
                    <TableRow key={d.id} data-testid={`doc-row-${i}`}>
                      <TableCell className="font-medium flex items-center gap-2">
                        {iconForFile(d.file_name)} {d.title}
                      </TableCell>
                      <TableCell className="capitalize">{d.category}</TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {AUDIENCIAS.find(a => a.value === d.audience_type)?.label || d.target_type || '—'}
                      </TableCell>
                      <TableCell><Badge className={stCfg.color}>{stCfg.label}</Badge></TableCell>
                      <TableCell className="text-xs text-slate-500">{(d.created_at || '').slice(0, 10)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {d.file_url && (
                          <>
                            <a href={`${apiBase}${d.file_url}`} target="_blank" rel="noreferrer" className="inline-block">
                              <Button size="sm" variant="ghost" data-testid={`btn-ver-${i}`}><Eye className="w-4 h-4" /></Button>
                            </a>
                            <a href={`${apiBase}${d.file_url}`} download={d.file_name}>
                              <Button size="sm" variant="ghost" data-testid={`btn-baixar-${i}`}><Download className="w-4 h-4" /></Button>
                            </a>
                          </>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => openEdit(d)} data-testid={`btn-editar-${i}`}><Edit className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => handleDelete(d)} data-testid={`btn-excluir-${i}`}><Trash2 className="w-4 h-4" /></Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </DnaTableV4>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="doc-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Documento' : 'Novo Documento'}</DialogTitle>
            <DialogDescription className="text-xs">Publique contratos, circulares, materiais e arquivos para as famílias.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Título *</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="input-title" />
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="input-desc" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Categoria *</Label>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="sel-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIAS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status *</Label>
                <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="sel-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Público *</Label>
              <Select value={form.audience_type} onValueChange={v => setForm(f => ({ ...f, audience_type: v }))}>
                <SelectTrigger data-testid="sel-audience"><SelectValue /></SelectTrigger>
                <SelectContent>{AUDIENCIAS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {form.audience_type === 'class_group' && (
              <div>
                <Label>Turmas (selecione 1 ou +)</Label>
                <div className="border rounded-md max-h-40 overflow-y-auto p-2 space-y-1">
                  {classGroups.map(cg => (
                    <label key={cg.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={form.class_group_ids.includes(cg.id)}
                        onCheckedChange={(c) => setForm(f => ({
                          ...f, class_group_ids: c ? [...f.class_group_ids, cg.id] : f.class_group_ids.filter(x => x !== cg.id)
                        }))}
                      />
                      <span>{cg.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {form.audience_type === 'segment' && (
              <div>
                <Label>Segmentos (separados por vírgula)</Label>
                <Input
                  placeholder="EF1, EF2, EM"
                  value={(form.segment || []).join(', ')}
                  onChange={e => setForm(f => ({ ...f, segment: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                />
              </div>
            )}

            <div>
              <Label>Arquivo (PDF, DOC, JPG, PNG, XLS · até {MAX_MB}MB) *</Label>
              <div className="flex items-center gap-2">
                <Input type="file" onChange={e => handleFile(e.target.files?.[0])} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx" data-testid="input-file" />
                {uploading && <Loader2 className="w-4 h-4 animate-spin" />}
              </div>
              {form.file_name && (
                <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                  {iconForFile(form.file_name)} {form.file_name} · {Math.round((form.file_size || 0) / 1024)} KB
                </p>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.notify_families} onCheckedChange={(c) => setForm(f => ({ ...f, notify_families: !!c }))} />
              Notificar famílias (envia notificação no DNA Connect)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-salvar">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
              {editing ? 'Salvar alterações' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
